import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  useFocusable,
  FocusContext,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import QRCode from "react-qr-code";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { logoutUser, setUser } from "../../redux/Slices/user.slice";
import { clearHomeShowcaseCache } from "../../utils/hooks/useHomeShowcaseData";
import {
  PROFILE_COLORS,
  PROFILE_FONT_SIZES,
  PROFILE_SPACING,
  PROFILE_RADIUS,
  PROFILE_SHADOWS,
  ICONS,
  FONT_FAMILY,
} from "./styles/profileStyles";
import ProfileSwitcherModal from "./ProfileSwitcherModal";

// ============================================================================
// STYLED COMPONENTS (Table-based layout for legacy TV compatibility)
// ============================================================================

var ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: ${PROFILE_SPACING.xl} ${PROFILE_SPACING.xxl};
  overflow-y: auto;
  font-family: ${FONT_FAMILY};
`;

var ProfileCard = styled.div`
  background: linear-gradient(
    135deg,
    ${PROFILE_COLORS.cardGradientStart} 0%,
    ${PROFILE_COLORS.cardGradientEnd} 100%
  );
  border-radius: ${PROFILE_RADIUS.lg};
  border: 1px solid ${PROFILE_COLORS.border};
  padding: ${PROFILE_SPACING.lg};
  margin: 0 auto;
  width: 100%;
`;

var SectionCard = styled.div`
  background: ${PROFILE_COLORS.sectionBg};
  border-radius: ${PROFILE_RADIUS.sm};
  padding: ${PROFILE_SPACING.md};
  margin-bottom: ${PROFILE_SPACING.md};
`;

// Table-based layout for user info
var UserTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

var UserTableCell = styled.td`
  vertical-align: middle;
  padding: 0;
`;

var Avatar = styled.div`
  width: 75px;
  height: 75px;
  border-radius: 50%;
  background: ${function (props) {
    return props.$hasImage
      ? "url(" + props.$imageUrl + ")"
      : PROFILE_COLORS.accentLight;
  }};
  background-size: cover;
  background-position: center;
  display: table;
`;

var AvatarInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
  color: ${PROFILE_COLORS.text};
  font-size: ${PROFILE_FONT_SIZES.avatar};
  font-weight: 600;
`;

var UserInfoWrapper = styled.div`
  padding-right: ${PROFILE_SPACING.lg};
`;

var UserName = styled.div`
  color: ${PROFILE_COLORS.textSecondary};
  font-size: ${PROFILE_FONT_SIZES.title};
  font-weight: 600;
  margin-bottom: ${PROFILE_SPACING.xs};
`;

var UserPhone = styled.div`
  color: ${PROFILE_COLORS.textSecondary};
  font-size: ${PROFILE_FONT_SIZES.subtitle};
  font-weight: 500;
`;

var Divider = styled.div`
  width: 1px;
  height: 60px;
  background: ${PROFILE_COLORS.divider};
  margin: 0 ${PROFILE_SPACING.lg};
`;

var SubscriptionInfo = styled.div`
  padding-right: ${PROFILE_SPACING.lg};
`;

var SubscriptionText = styled.div`
  color: ${props => props.$hasSubscription ? PROFILE_COLORS.text : PROFILE_COLORS.textSecondary};
  font-size: ${PROFILE_FONT_SIZES.subtitle};
  font-weight: ${props => props.$hasSubscription ? "700" : "400"};
`;

var SubscriptionDays = styled.span`
  color: ${props => props.$isLow ? '#ff4444' : '#44cc44'};
  font-weight: 700;
`;

var QRSection = styled.div`
  text-align: center;
  width: 256px;
`;

var QRLabel = styled.div`
  color: lightgray;
  font-size: ${PROFILE_FONT_SIZES.subtitle};
  margin-bottom: ${PROFILE_SPACING.sm};
  font-weight:900;
  text-align: center;
`;

var QRWrapper = styled.div`
  width: 256px;
  padding: 8px;
  background: ${PROFILE_COLORS.textSecondary};
  border-radius: ${PROFILE_RADIUS.sm};
`;

// Menu Grid - Table based
var MenuTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: ${PROFILE_SPACING.md};
  margin-top: ${PROFILE_SPACING.md};
`;

var MenuRow = styled.tr``;

var MenuCell = styled.td`
  width: 25%;
  vertical-align: top;
`;

// Focusable Button Component
var MenuButtonStyled = styled.button`
  display: block;
  width: 100%;
  background: ${props => props.$danger ? PROFILE_COLORS.danger : PROFILE_COLORS.buttonBg};
  border: none;
  border-radius: ${PROFILE_RADIUS.lg};
  padding: ${PROFILE_SPACING.lg} ${PROFILE_SPACING.md};
  color: ${props => props.$danger ? PROFILE_COLORS.text : PROFILE_COLORS.textSecondary};
  font-size: ${PROFILE_FONT_SIZES.subtitle};
  font-weight: 900;
  font-family: inherit;
  text-align: center;
  cursor: pointer;
  border: ${props =>
    props.$focused
      ? PROFILE_SHADOWS.focusInset()
      : PROFILE_SHADOWS.none};
`;

var ButtonIcon = styled.span`
  margin-left: ${PROFILE_SPACING.sm};
  font-size: 24px;
`;

var SubscriptionButton = styled.button`
  display: block;
  width: 100%;
  background: ${PROFILE_COLORS.success};
  border: none;
  border-radius: ${PROFILE_RADIUS.lg};
  padding: ${PROFILE_SPACING.lg} ${PROFILE_SPACING.xl};
  color: ${PROFILE_COLORS.text};
  font-size: ${PROFILE_FONT_SIZES.subtitle};
  font-weight: 900;
  font-family: inherit;
  text-align: center;
  cursor: pointer;
  border: ${props =>
    props.$focused
      ? PROFILE_SHADOWS.focusInset()
      : PROFILE_SHADOWS.none};
  margin-top: ${PROFILE_SPACING.md};
`;

// ============================================================================
// FOCUSABLE MENU BUTTON COMPONENT
// ============================================================================

function FocusableMenuButton({ label, icon, onPress, danger, focusKey }) {
  var { ref, focused } = useFocusable({
    focusKey: focusKey,
    onEnterPress: onPress,
  });

  return (
    <MenuButtonStyled
      ref={ref}
      onClick={onPress}
      $focused={focused}
      $danger={danger}
    >
      {icon && <ButtonIcon>{icon}</ButtonIcon>}
      {label}
    </MenuButtonStyled>
  );
}

// ============================================================================
// FOCUSABLE SUBSCRIPTION BUTTON
// ============================================================================

function FocusableSubscriptionButton({ onPress }) {
  var { ref, focused } = useFocusable({
    focusKey: "buy-subscription",
    onEnterPress: onPress,
  });

  return (
    <SubscriptionButton ref={ref} onClick={onPress} $focused={focused}>
      <ButtonIcon>{ICONS.cart}</ButtonIcon>
      خرید اشتراک
    </SubscriptionButton>
  );
}

// ============================================================================
// MAIN PROFILE CONTENT COMPONENT
// ============================================================================

var ProfilePageContent = function (props) {
  var data = props.data;
  var user = useSelector(function (state) { return state.User; });
  var dispatch = useDispatch();
  var navigate = useNavigate();

  var { ref, focusKey } = useFocusable({
    focusKey: "PROFILE_CONTENT",
    isFocusBoundary: false,
    preferredChildFocusKey: "menu-bookmarks",
  });

  // Get subscription days from API response
  var subDays = useMemo(function () {
    try {
      var activeSubscription = data.activeSubscription;
      if (!activeSubscription) {
        return "";
      }
      // Use remainingAccessDays from the new API
      if (typeof activeSubscription.remainingAccessDays === 'number') {
        return activeSubscription.remainingAccessDays;
      }
      // Fallback to calculating from expireDate if available
      if (activeSubscription.subscriptionInfo?.expireDate) {
        var currentDate = new Date();
        var expireDate = new Date(activeSubscription.subscriptionInfo.expireDate);
        var timeDiff = expireDate - currentDate;
        return Math.round(timeDiff / (1000 * 60 * 60 * 24));
      }
      return "";
    } catch (error) {
      return "";
    }
  }, [data]);

  // Set user data on mount
  useEffect(function () {
    if (data) {
      dispatch(setUser(data));
    }
  }, [data, dispatch]);

  // Set initial focus
  useEffect(function () {
    var timer = setTimeout(function () {
      setFocus("menu-bookmarks");
    }, 150);
    return function () { clearTimeout(timer); };
  }, []);

  // Navigation handlers
  function handleFavourites() {
    navigate("/profile/favourites");
  }

  function handleWatchHistory() {
    navigate("/profile/watch-history");
  }

  function handleSubscriptions() {
    navigate("/profile/subscriptions");
  }

  function handleBuySubscription() {
    navigate("/subscription");
  }

  function handleLogout() {
    dispatch(logoutUser());
    localStorage.removeItem("webTvUserToken");
    localStorage.removeItem("webTvrefreshToken");
    clearHomeShowcaseCache();
    toast.success("با موفقیت از حساب خود خارج شدید");
    navigate("/");
  }

  // Profile switcher modal state
  var [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
  var profiles = data?.profiles || [];
  var currentProfileId = data?.currentProfileId || data?._id;

  function handleSwitchProfile() {
    if (profiles.length === 0) {
      toast.error("پروفایل دیگری یافت نشد");
      return;
    }
    setShowProfileSwitcher(true);
  }

  function handleCloseSwitcher() {
    setShowProfileSwitcher(false);
    setTimeout(function () {
      setFocus("menu-switch-profile");
    }, 150);
  }

  // Get user display info
  var userName = user?.full_name || "کاربر سایت";
  var userInitial = userName.charAt(0) || "ک";
  var mobileNumber = user?.mobileNumber ? "0" + user.mobileNumber : "";
  var profileImage = data?.profileImage || user?.profileImage;
  var hasProfileImage = profileImage && profileImage.length > 0;
  var hasSubscription = subDays && subDays > 0;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentWrapper ref={ref}>
        <ProfileCard>
          {/* User Info Section */}
          <SectionCard>
            <UserTable>
              <tbody>
                <tr>
                  {/* Avatar */}
                  <UserTableCell style={{ width: "90px" }}>
                    <Avatar $hasImage={hasProfileImage} $imageUrl={profileImage}>
                      {!hasProfileImage && <AvatarInner>{userInitial}</AvatarInner>}
                    </Avatar>
                  </UserTableCell>

                  {/* User Name & Phone */}
                  <UserTableCell style={{ width: "200px" }}>
                    <UserInfoWrapper>
                      <UserName>{userName}</UserName>
                      <UserPhone>{mobileNumber}</UserPhone>
                    </UserInfoWrapper>
                  </UserTableCell>

                  {/* Divider */}
                  <UserTableCell style={{ width: "50px" }}>
                    <Divider />
                  </UserTableCell>

                  {/* Subscription Info */}
                  <UserTableCell>
                    <SubscriptionInfo>
                      {hasSubscription ? (
                        <SubscriptionText $hasSubscription={true}>
                          <SubscriptionDays $isLow={subDays < 10}>{subDays}</SubscriptionDays> روز از اشتراک باقی مانده
                        </SubscriptionText>
                      ) : (
                        <SubscriptionText $hasSubscription={false}>
                          {ICONS.warning} اشتراک فعال ندارید
                        </SubscriptionText>
                      )}
                    </SubscriptionInfo>
                  </UserTableCell>

                  {/* QR Code (only when has subscription) */}
                  {hasSubscription && (
                    <UserTableCell style={{ width: "280px" }}>
                      <QRSection>
                        <QRLabel>خرید اشتراک</QRLabel>
                        <QRWrapper>
                          <QRCode
                            size={240}
                            style={{ width: "100%", height: "auto" }}
                            value="https://mnbx.ir/subscription"
                            bgColor={PROFILE_COLORS.textSecondary}
                            fgColor={PROFILE_COLORS.background}
                            viewBox="0 0 256 256"
                          />
                        </QRWrapper>
                      </QRSection>
                    </UserTableCell>
                  )}
                </tr>
              </tbody>
            </UserTable>
          </SectionCard>

          {/* Menu Grid */}
          <MenuTable>
            <tbody>
              <MenuRow>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-bookmarks"
                    label="نشان شده ها"
                    onPress={handleFavourites}
                  />
                </MenuCell>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-history"
                    label="تاریخچه تماشا"
                    onPress={handleWatchHistory}
                  />
                </MenuCell>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-switch-profile"
                    label="تغییر پروفایل"
                    onPress={handleSwitchProfile}
                  />
                </MenuCell>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-logout"
                    label="خروج از حساب"
                    onPress={handleLogout}
                    danger={true}
                  />
                </MenuCell>
              </MenuRow>
            </tbody>
          </MenuTable>

          {/* Buy Subscription Button (when no subscription) */}
          {!hasSubscription && (
            <FocusableSubscriptionButton onPress={handleBuySubscription} />
          )}
        </ProfileCard>
      </ContentWrapper>

      {/* Profile Switcher Modal */}
      <ProfileSwitcherModal
        open={showProfileSwitcher}
        profiles={profiles}
        currentProfileId={currentProfileId}
        onClose={handleCloseSwitcher}
      />
    </FocusContext.Provider>
  );
};

export default ProfilePageContent;
